import React from "react";
import { Box, Text, UnstyledButton } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { ReportMetricDef } from "../../../../types/reportsV2";
import { lineItemsLabel } from "../../v2/ReportActionRow";
import { ReportCardNode } from "./cardModel";
import MetricRows from "./MetricRows";
import { CardExpansion } from "./useCardExpansion";
import styles from "./MobileCards.module.css";

export interface MobileReportCardProps {
  node: ReportCardNode;
  /** Tree level: 0 for date buckets; groups are indented one step each. */
  depth: number;
  /** Visible columns, in view order. */
  columns: ReportMetricDef[];
  /** Shown beside the title while the card is closed. */
  primary: ReportMetricDef | null;
  expansion: CardExpansion;
}

/**
 * One node of the report tree as a card: a date bucket, or a group indented
 * under its parent. Closed, it shows the label, line count and the primary
 * value; tapping opens the other visible columns as label / value rows (and
 * lists the node's groups under it — see MobileCardList).
 */
export default function MobileReportCard({
  node,
  depth,
  columns,
  primary,
  expansion,
}: MobileReportCardProps) {
  const open = expansion.isOpen(node.id);
  const rest = columns.filter((c) => c.key !== primary?.key);
  return (
    <Box
      className={styles.card}
      data-depth={depth}
      ml={depth > 0 ? `calc(var(--mantine-spacing-sm) * ${depth})` : undefined}
    >
      <UnstyledButton
        type="button"
        className={styles.cardHeader}
        aria-expanded={open}
        onClick={() => expansion.toggle(node.id)}
      >
        <Box component="span" className={styles.cardTitles}>
          <Text component="span" className={styles.cardTitle}>
            {node.label}
          </Text>
          <Text component="span" className={styles.cardSub}>
            {lineItemsLabel(node.lineCount)}
          </Text>
        </Box>
        {primary && (
          <Box component="span" className={styles.cardValue}>
            <Text component="span" className={styles.cardValueText}>
              {node.cells[primary.key] ?? "—"}
            </Text>
            <Text component="span" className={styles.cardValueLabel}>
              {primary.label}
            </Text>
          </Box>
        )}
        <IconChevronDown
          size={17}
          className={styles.chevron}
          data-open={open || undefined}
          aria-hidden
        />
      </UnstyledButton>
      {open && rest.length > 0 && (
        <Box className={styles.cardBody}>
          <MetricRows columns={rest} cells={node.cells} />
        </Box>
      )}
    </Box>
  );
}
